import React, { useRef, useState } from 'react';

import type { AlignmentType } from '@aline/common';
import { AlineTransforms } from '@aline/common';
import { Divider, FormControlLabel, Paper, Popover, Typography } from '@mui/material';
import { AlineCheckbox, SelectButton } from 'src/components';
import { PageNumbersIcon } from 'src/components/icons/PageNumbersIcon';
import { SmallDownArrowIcon } from 'src/components/icons/SmallDownArrowIcon';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql/hooks';
import { useEditor, useEditorLoading } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  alignmentOption: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
      borderColor: theme.appColors.primary,
    },
    '&:nth-child(3n+2)': {
      justifyContent: 'center',
    },
    '&:nth-child(3n+3)': {
      justifyContent: 'flex-end',
    },
    '&:nth-child(n+4)': {
      alignItems: 'flex-end',
    },
    alignItems: 'flex-start',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 4,
    boxSizing: 'border-box',
    color: theme.appTypography.fieldLabel.color,
    cursor: 'pointer',
    display: 'flex',
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
    height: 80,
    justifyContent: 'flex-start',
    padding: theme.spacing(0.5),
    textAlign: 'left',
    width: 64,
  },
  alignmentOptionsContainer: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridTemplateRows: 'repeat(2, 1fr)',
    padding: theme.spacing(1),
  },
  arrowIcon: {
    height: 12,
    marginLeft: theme.spacing(0.5),
    width: 12,
  },
  formControlLabel: {
    marginLeft: 0,
    marginRight: 0,
  },
  pageCountContainer: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing(1),
  },
  pageNumbersText: {
    marginLeft: theme.spacing(0.5),
  },
  removeButton: {
    justifyContent: 'flex-start',
    paddingBottom: theme.spacing(1.75),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(1.75),
    width: '100%',
  },
  selectButton: {
    height: 32,
    width: 134,
  },
}));

export function PageNumbersButton(): JSX.Element {
  const { classes } = useStyles();
  const editorLoading = useEditorLoading();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);

  const [isIncludePageCount, setIsIncludePageCount] = useState<boolean>(false);

  const alignmentText = isIncludePageCount ? '1 of 1' : '1';

  const handleInsertPageNumbers = (
    alignment: AlignmentType,
    location: 'header' | 'footer' = 'header'
  ) => {
    if (!currentUserId || !editingMode) return;

    setPopoverOpen(false);
    AlineTransforms.insertPageNumbering(editor, {
      alignment,
      authorId: currentUserId,
      editingMode,
      includePageCount: isIncludePageCount,
      location,
    });
    ClientTransforms.focus(editor);
  };

  const handleRemovePageNumbers = () => {
    if (!currentUserId || !editingMode) return;

    setPopoverOpen(false);
    AlineTransforms.removePageNumbering(editor, {
      authorId: currentUserId,
      editingMode,
    });
    ClientTransforms.focus(editor);
  };

  return (
    <>
      <SelectButton
        ref={buttonRef}
        className={classes.selectButton}
        disabled={editorLoading}
        selected={popoverOpen}
        onClick={() => setPopoverOpen(!popoverOpen)}
      >
        <PageNumbersIcon />
        <Typography className={classes.pageNumbersText} color="inherit" variant="caption">
          Page Numbers
        </Typography>
        <SmallDownArrowIcon className={classes.arrowIcon} />
      </SelectButton>
      <Popover
        anchorEl={buttonRef.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={popoverOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => {
          setPopoverOpen(false);
        }}
      >
        <Paper>
          <div className={classes.alignmentOptionsContainer}>
            <button
              aria-label="Header - Left"
              className={classes.alignmentOption}
              type="button"
              onClick={() => handleInsertPageNumbers('left')}
            >
              <span aria-hidden>{alignmentText}</span>
            </button>
            <button
              aria-label="Header - Center"
              className={classes.alignmentOption}
              type="button"
              onClick={() => handleInsertPageNumbers('center')}
            >
              <span aria-hidden>{alignmentText}</span>
            </button>
            <button
              aria-label="Header - Right"
              className={classes.alignmentOption}
              type="button"
              onClick={() => handleInsertPageNumbers('right')}
            >
              <span aria-hidden>{alignmentText}</span>
            </button>
            <button
              aria-label="Footer - Left"
              className={classes.alignmentOption}
              type="button"
              onClick={() => handleInsertPageNumbers('left', 'footer')}
            >
              <span aria-hidden>{alignmentText}</span>
            </button>
            <button
              aria-label="Footer - Center"
              className={classes.alignmentOption}
              type="button"
              onClick={() => handleInsertPageNumbers('center', 'footer')}
            >
              <span aria-hidden>{alignmentText}</span>
            </button>
            <button
              aria-label="Footer - Right"
              className={classes.alignmentOption}
              type="button"
              onClick={() => handleInsertPageNumbers('right', 'footer')}
            >
              <span aria-hidden>{alignmentText}</span>
            </button>
          </div>
          <div className={classes.pageCountContainer}>
            <FormControlLabel
              className={classes.formControlLabel}
              control={
                <AlineCheckbox
                  checked={isIncludePageCount}
                  color="primary"
                  size="medium"
                  onChange={() => {
                    setIsIncludePageCount(!isIncludePageCount);
                  }}
                />
              }
              label="Include Page Count"
            />
          </div>
          <Divider />
          <SelectButton className={classes.removeButton} onClick={handleRemovePageNumbers}>
            <Typography align="left">Remove Page Numbers</Typography>
          </SelectButton>
        </Paper>
      </Popover>
    </>
  );
}
